import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { setPreparedCanvasAction } from '../../../redux/actions/collaboration';
import Cropper from 'react-cropper';
import html2canvas from 'html2canvas';
import KubitTooltip from '../../KubitTooltip';
import { Button, Dimmer, Loader } from 'semantic-ui-react';
import styled from 'styled-components';
import 'cropperjs/dist/cropper.css';
import { ignoreCanvasElements } from '../Screenshot/utils';
import { SvgIcon } from '../../Icon';
import { theme } from '../../../styles/theme';
import { HEADER_Z_INDEX, SIDEBAR_Z_INDEX } from '../../../styles/zIndexes';

interface IProps {
  hasContainer: boolean;
  updateZIndices?: boolean;
}

const CameraIconContainer = styled(({ ...props }) => <Button {...props} />)`
  &&& {
    width: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 !important;
    height: 24px;
    border-radius: 18px;
    background-image: linear-gradient(
      325deg,
      #0076ea -28%,
      ${theme.colors.mainBlue} -95%
    );
    i {
      color: white !important;
      opacity: 1 !important;
    }
  }
`;

const CropContainer = styled.div`
  width: 100vw;
  height: 100vh;
  position: fixed;
  left: 0;
  top: 0;
  z-index: 2000;
`;

const CropHint = styled.div`
  padding: 4px 8px;
  border-radius: 4px;
  background: white;
  z-index: 2001;
  position: fixed;
  overflow: hidden;
  max-width: 200px;
`;
export default ({ hasContainer, updateZIndices = false }: IProps) => {
  const dispatch = useDispatch();
  const [isScreenshot, setIsScreenshot] = useState(false);
  const [imageCanvas, setImageCanvas] = useState<HTMLCanvasElement | undefined>(
    undefined,
  );
  const cropperRef = useRef<any>(null);
  const mouseMoveHandler = (event: any, isCropMove?: boolean) => {
    const hintContainer: HTMLElement | null = document.querySelector(
      '.CropperHintContainer',
    );
    const cropBox: HTMLElement | null =
      document.querySelector('.cropper-crop-box');
    if (hintContainer && !isCropMove) {
      hintContainer.style.display = 'block';
      hintContainer.style.left = event.clientX + 10 + 'px';
      hintContainer.style.top = event.clientY + 10 + 'px';
    }
    if (cropBox && hintContainer && isCropMove) {
      const cropBoxRect = cropBox.getBoundingClientRect();
      hintContainer.style.display = 'block';
      hintContainer.style.left = cropBoxRect.right + 10 + 'px';
      hintContainer.style.top = cropBoxRect.bottom + 10 + 'px';
    }
  };

  useEffect(() => {
    const takeScreenshot = async () => {
      const body = document.getElementById('root');
      if (body) {
        /* YAxis padding overlapping the last cartesian grid line.
         It is fixed with css however the html2canvas ignores it.
        Find all problematic lines and add the html2canvas
        ignore attribute to them before rendering the screenshot */
        document
          .querySelectorAll(
            '.recharts-surface .recharts-cartesian-grid .recharts-cartesian-grid-horizontal > line:nth-child(6)',
          )
          ?.forEach((line: any) =>
            line.setAttribute('data-html2canvas-ignore', 'true'),
          );

        const canvas = await html2canvas(body, {
          ignoreElements: ignoreCanvasElements,
          scale: 2,
          useCORS: true,
          logging: false,
        });

        setImageCanvas(canvas);
      }
    };

    if (isScreenshot) {
      takeScreenshot();
      document.addEventListener('mousemove', mouseMoveHandler);
    }

    return () => {
      document.removeEventListener('mousemove', mouseMoveHandler);
    };
  }, [isScreenshot]);

  const resetScreenshotCanvasState = () => {
    setIsScreenshot(false);
    setImageCanvas(undefined);
  };

  const onCropEnd = () => {
    const imageElement: any = cropperRef?.current;
    const cropper: any = imageElement?.cropper;
    // if crop box is empty, return
    const cropBox = cropper.getCropBoxData();
    if (Object.keys(cropBox).length === 0) {
      return;
    }
    dispatch(
      setPreparedCanvasAction(
        cropper?.getCroppedCanvas({
          width: cropBox.width,
          height: cropBox.height,
          imageSmoothingEnabled: true,
          imageSmoothingQuality: 'high',
        }),
      ),
    );
    resetScreenshotCanvasState();
  };

  useEffect(() => {
    const header = document.getElementsByClassName('header')[0] as any;
    const sidebar = document.getElementById('sidebar-container') as any;
    const workspaceHeader = document.getElementById('workspace-header') as any;
    const right = document.getElementById(
      'collaboration-right-container',
    ) as any;
    const contentWrapper = document.getElementById(
      'main-content-wrapper',
    ) as any;
    const mainContainer = document.getElementById('main-container') as any;

    const clearCropBox = (event: any) => {
      if (event.key === 'Escape' && cropperRef) {
        // add question mark to avoid error that clear and crop happen together.
        if (cropperRef.current?.cropper?.cropping) {
          cropperRef.current.cropper.clear();
        } else {
          resetScreenshotCanvasState();
        }
      }
    };

    if (imageCanvas) {
      header.style.zIndex = 0;
      right.style.zIndex = 0;
      sidebar.style.zIndex = 0;

      if (contentWrapper) {
        contentWrapper.style.overflow = 'unset';
      }
      if (mainContainer) {
        mainContainer.style.overflowY = 'unset';
      }
      if (updateZIndices) {
        workspaceHeader.style.zIndex = 0;
      }

      document.body.addEventListener('keydown', clearCropBox);
    }

    return () => {
      header.style.zIndex = HEADER_Z_INDEX;
      right.style.zIndex = SIDEBAR_Z_INDEX;
      sidebar.style.zIndex = SIDEBAR_Z_INDEX;

      if (contentWrapper) {
        contentWrapper.style.overflow = 'hidden';
      }
      if (mainContainer) {
        mainContainer.style.overflowY = 'auto';
      }
      if (updateZIndices) {
        workspaceHeader.style.zIndex = SIDEBAR_Z_INDEX;
      }

      document.body.removeEventListener('keydown', clearCropBox);
    };
  }, [imageCanvas, updateZIndices]);

  return (
    <>
      {isScreenshot && !imageCanvas && (
        <Dimmer active inverted data-html2canvas-ignore="true">
          <Loader />
        </Dimmer>
      )}
      {hasContainer ? (
        <KubitTooltip content={'Take a screenshot.'} noOpenDelay={true}>
          <CameraIconContainer onClick={() => setIsScreenshot(true)}>
            <SvgIcon
              name="camera"
              fill="#FFF"
              activeColor="#FFF"
              isActive
              width={14}
              height={12}
            />
          </CameraIconContainer>
        </KubitTooltip>
      ) : (
        <KubitTooltip content={'Take a screenshot.'} noOpenDelay={true}>
          <SvgIcon
            name="camera"
            onClick={() => setIsScreenshot(true)}
            fill="#626266"
          />
        </KubitTooltip>
      )}

      {isScreenshot && imageCanvas && (
        <CropContainer>
          <Cropper
            src={imageCanvas.toDataURL('image/png')}
            autoCrop={false}
            ref={cropperRef}
            minCropBoxHeight={1}
            minCropBoxWidth={1}
            style={{ height: '100%', width: '100%', opacity: 1 }}
            guides={false}
            zoomable={false}
            cropend={() => onCropEnd()}
            movable={false}
            cropmove={(_) => mouseMoveHandler(_, true)}
          />
          <CropHint className="CropperHintContainer">
            Use mouse to select the screen area to annotate, Use ESC to cancel.
          </CropHint>
        </CropContainer>
      )}
    </>
  );
};
